import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

export interface ActionsResponse {
  actions: CorrectiveAction[];
  counts: {
    in_progress: number;
    total: number;
    pending: number;
    cancelled: number;
    completed: number;
    unassigned: number;
    archived: number;
  };
  total_elements: number;
  total_pages: number;
  current_page: number;
  size: number;
}

export interface CorrectiveAction {
  id?: string;
  action_id: string;
  action_number: string;
  title?: string;
  trigger_reason: string;
  status: 'pending' | 'in_progress' | 'completed' | 'cancelled';
  description: string;
  action_taken?: string;
  success_criteria?: string;
  priority?: 'low' | 'medium' | 'high';
  is_corrective: boolean;
  assigned_user_id?: string;
  assigned_user?: {
    user_id: string;
    first_name: string;
    last_name: string;
  };
  assigned_to?: string;
  due_date?: string;
  patient_feedback_id?: string;
  patient_feedback?: {
    feedback_id: string;
    created_at: string;
    comment?: string;
    score: number;
    reported_bucket: number;
  };
  feedback_ids?: string[];
  total_steps?: number;
  completed_steps?: number;
  created_at: string;
  updated_at: string;
  completed_at?: string;
  /** Renseigné côté client lors du chargement (mono-site ou agrégé) — absent du payload backend. */
  establishment_id?: string;
}

export interface CreateActionRequest {
  description: string;
  trigger_reason: string;
  success_criteria?: string;
  is_corrective: boolean;
  assigned_user_id?: string;
  patient_feedback_id?: string;
  status?: 'pending' | 'in_progress' | 'completed' | 'cancelled';
}

export interface UpdateActionRequest {
  title?: string;
  description?: string;
  priority?: 'low' | 'medium' | 'high';
  due_date?: string;
  assigned_user_id?: string;
  status?: 'pending' | 'in_progress' | 'completed' | 'cancelled';
  completed_steps?: number;
  action_taken?: string;
  success_criteria?: string;
}

@Injectable({
  providedIn: 'root'
})
export class CorrectiveActionsService {
  private http = inject(HttpClient);
  private baseUrl = `${environment.apiUrl}/api/v1/corrective-actions`;

  getActions(establishmentId: string): Observable<ActionsResponse> {
    return this.http.get<ActionsResponse>(`${this.baseUrl}/establishments/${establishmentId}`);
  }

  createAction(establishmentId: string, action: CreateActionRequest): Observable<{data: {action_id: string}, success: boolean, message: string}> {
    return this.http.post<{data: {action_id: string}, success: boolean, message: string}>(`${this.baseUrl}/establishments/${establishmentId}`, action);
  }

  updateAction(actionId: string, updates: UpdateActionRequest): Observable<CorrectiveAction> {
    return this.http.put<CorrectiveAction>(`${this.baseUrl}/${actionId}`, updates);
  }

  updateActionStatus(actionId: string, status: CorrectiveAction['status']): Observable<void> {
    return this.http.patch<void>(`${this.baseUrl}/${actionId}/status`, { status });
  }

  deleteAction(actionId: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/${actionId}`);
  }

  archiveAction(actionId: string): Observable<{success: boolean, message: string}> {
    return this.http.put<{success: boolean, message: string}>(`${this.baseUrl}/${actionId}/archive`, {});
  }

  exportArchivedActions(establishmentId: string): Observable<Blob> {
    return this.http.get(`${this.baseUrl}/establishments/${establishmentId}/archived/export`, {
      responseType: 'blob'
    });
  }

  getUsers(establishmentId: string): Observable<{id: string, name: string, email: string}[]> {
    return this.http.get<{id: string, name: string, email: string}[]>(
      `${environment.apiUrl}/api/v1/establishments/${establishmentId}/actualite/users`
    );
  }
}
